import palette from '@src/theme/palette';
import {
  ActiveActionType,
  EnrichedClaimType,
  TextAnalysisResultsType,
} from '@src/types/main';

export const generateId = (index: number) => `claim-${index}`;

// Utility to normalize text for matching, but not for display
export const normalizeForMatching = (text: string) =>
  text.replace(/\s+/g, ' ').trim();

export const findOriginalIndices = (
  original: string,
  normStart: number,
  normEnd: number
) => {
  let origStart = -1;
  let origEnd = -1;
  let normIndex = 0;

  // Convert the original string to an array of characters and use forEach
  original.split('').forEach((char, i, arr) => {
    // Skip counting whitespace in the normalized version
    if (!/\s/.test(char) || (i > 0 && !/\s/.test(arr[i - 1]))) {
      if (normIndex === normStart && origStart === -1) {
        origStart = i;
      }
      if (normIndex === normEnd && origEnd === -1) {
        origEnd = i;
      }
      normIndex += 1;
    }
  });

  return { startIndex: origStart, endIndex: origEnd };
};

export const generatePartialIndices = (str: string, partial: string) => {
  // Normalize both strings for matching
  const normalizedStr = normalizeForMatching(str);
  const normalizedPartial = normalizeForMatching(partial);

  const normStartIndex = normalizedStr.indexOf(normalizedPartial);
  if (normStartIndex === -1) {
    return { startIndex: -1, endIndex: -1 };
  }

  const normEndIndex = normStartIndex + normalizedPartial.length - 1;

  // Map the indices back to the original string
  return findOriginalIndices(str, normStartIndex, normEndIndex);
};

export const generateColors = (action: ActiveActionType) => {
  const colors = { text: '', bg: '' };
  if (action === 'claim_detection') {
    colors.text = palette.accent.dark;
    colors.bg = palette.accent.light;
  }

  if (action === 'claim_matching') {
    colors.text = palette.primary.dark;
    colors.bg = palette.secondary.light;
  }

  return colors;
};

export const generateLabel = (action: ActiveActionType, idx: number) => {
  const number = idx + 1;

  if (action === 'claim_detection') {
    return `${number}. Claim Detection`;
  }

  if (action === 'claim_matching') {
    return `${number}. Claim Matching`;
  }

  return `${number}.`;
};

export const generateAttributes = (
  analysisResults: TextAnalysisResultsType,
  actionType: ActiveActionType
): EnrichedClaimType[] => {
  if (!analysisResults.message || !analysisResults.claims.length) return [];

  const enrichedClaims = analysisResults.claims.map((claim, idx) => {
    const { startIndex, endIndex } = generatePartialIndices(
      analysisResults.message,
      claim.claim
    );

    return {
      id: generateId(idx),
      claim: claim.claim,
      startIndex,
      endIndex,
      label: generateLabel(actionType, idx),
      colors: generateColors(actionType),
    };
  });

  const sortedClaims = enrichedClaims.toSorted(
    (a, b) => a.startIndex - b.startIndex
  );

  return sortedClaims;
};
